import { useCallback, useMemo } from "react"
import { GetApp } from "../../App"
import { computeEditHistory } from "../../App/EditHistory"
import { compositeField, FieldFormat } from "../../App/Format"
import { GetShipUsers } from "../ShipView"
import { Block, BlockField, BlockFieldInputsDivider, BlockRow } from "../../Components/Block"
import { EditHistory } from "../../Components/EditHistory"
import { Icon } from "../../Components/Icon"
import { ShipUserSelectData } from "../../Components/ShipUserSelect"
import { Day, IAuditLogEditSource, IDayWatchGroup, IDayWatchShift, INote, INoteType, IShipStatus, IShipUser, IStatusType } from "../../Types"
import { dayToDate } from "../DayView"
import { TrackView } from "../TrackView"
import { TrackViewData } from "../TrackView/Layer"
import { DayListType, GetData, GetPrintViewContext } from "./Context"
import { PrintDaysViewProps } from "./Params"
import { PrintCalculationsView } from "./CalculationsView"
import { PrintLogListView } from "./LogListView"
import { PrintLogView } from "./LogView"
import { PrintNoteView } from "./NoteView"
import { PrintWatchGroupView } from "./WatchGroupView"
import { PrintWatchShiftView } from "./WatchShiftView"
import { dayShipStatusSummary, shipStatusAtLog } from "../Log/status"

// Live current value for a `DayEditableField`, used by `computeEditHistory` to
// resolve the "new value" of each field's most recent edit.
// eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: replace `any` with a concrete type
function currentDayFieldValue(day: any, field: string): unknown {
  switch (field) {
    case "comment": case "crew": case "endLocation": case "passengers":
    case "skipper": case "startLocation":
      return day[field];
    default:
      return undefined;
  }
}

// Per-field value formatters for Day fields (issue #529).
function dayFieldFormat(shipUsers: IShipUser[] | undefined): FieldFormat {
  const userLabel = compositeField(
    (value): value is number => typeof value === "number",
    (id): string => {
      const user = shipUsers?.find((u) => u.id == id);
      return user ? ShipUserSelectData.userLabel(user) : "–";
    },
  );
  const userList = compositeField(
    (value): value is unknown[] => Array.isArray(value),
    (ids): string => (ids.length > 0 ? ids.map((id) => userLabel(id)).join(", ") : "–"),
  );
  return {
    crew: userList,
    passengers: userList,
    skipper: userLabel,
  };
}

export function PrintDaysView({ day, excludeDetailed: _excludeDetailed }: PrintDaysViewProps) {
  const app = GetApp();
  const context = GetPrintViewContext();
  const data = GetData();
  const shipUsers = GetShipUsers();
  const date = dayToDate(day.day);
  const t = app.getT("DayEditView");
  const tc = app.getT("PrintView");

  const dayStatuses = useMemo(() => {
    return data?.statuses?.filter((s) => s.shipId === day.shipId && s.dayId === day.id && s.deletedAt == undefined) ?? [];
  }, [data?.statuses, day.shipId, day.id]);

  const shipStatuses = useMemo(() => {
    return dayStatuses.filter((s) => s.statusType == IStatusType.ship);
  }, [dayStatuses]);

  const getUserName = useCallback((userId?: number) => {
    if (!userId || !shipUsers) return "";
    const user = shipUsers.find((u) => u.id === userId);
    return user ? `${user.name || ""}` : "";
  }, [shipUsers]);

  const getCrewNames = useCallback((crewIds?: number[]) => {
    if (!crewIds || crewIds.length === 0 || !shipUsers) return "";
    return crewIds.map((id) => {
      const user = shipUsers.find((u) => u.id === id);
      return user ? `${user.name || ""}` : "";
    }).filter((name) => name).join(", ");
  }, [shipUsers]);

  const statusButton = useCallback((status: IShipStatus) => {
    const statusName = IShipStatus[status];
    if (status == IShipStatus.none) {
      return app.t(`LogNavigationView_status_${statusName}`);
    }
    return <><Icon icon={`status-${statusName}`} /> {app.t(`LogNavigationView_status_${statusName}`)}</>;
  }, [app]);

  const { dayStatus, overnightStatus } = useMemo(() => {
    return dayShipStatusSummary(day, data?.statuses, data?.logs);
  }, [day, data?.statuses, data?.logs]);

  const tracks = useMemo(() => {
    if (!data) {
      return [];
    }
    return data.tracks.filter((track) => track.dayId == day.id);
  }, [data, day.id]);

  const weatherNotes = useMemo(() => {
    return (day.notes || []).filter((note) => note.dayId == day.id && note.noteType === INoteType.weather && (note.note || note.noteExtra || note.title || note.date)).map((note: INote) => {
      return <PrintNoteView key={note.id} day={day} note={note} />;
    });
  }, [day]);

  const journalNotes = useMemo(() => {
    return (day.notes || []).filter((note) => note.dayId == day.id && note.noteType === INoteType.journal && (note.title || note.note)).map((note: INote) => {
      return <PrintNoteView key={note.id} day={day} note={note} />;
    });
  }, [day]);

  const technicalNotes = useMemo(() => {
    return (day.notes || []).filter((note) => note.dayId == day.id && note.noteType === INoteType.technical && (note.title || note.note)).map((note: INote) => {
      return <PrintNoteView key={note.id} day={day} note={note} />;
    });
  }, [day]);

  const watchGroups = useMemo(() => {
    const data = Day.parse(day).dataParsed;
    if (!data.watch || !data.watch.groups) {
      return [];
    }
    return data.watch.groups.map((group: IDayWatchGroup, index: number) => {
      return <PrintWatchGroupView key={group.id} group={group} index={index} />;
    });
  }, [day]);

  const watchShifts = useMemo(() => {
    const data = Day.parse(day).dataParsed;
    if (!data.watch || !data.watch.shifts) {
      return [];
    }
    return data.watch.shifts.map((shift: IDayWatchShift, index: number) => {
      return <PrintWatchShiftView key={shift.start} shift={shift} index={index} />;
    });
  }, [day]);

  const logs = useMemo(() => {
    if (!data) {
      return [];
    }
    return data.logs.filter((log) => log.dayId == day.id).map((log) => {
      const status = shipStatusAtLog(shipStatuses, log.id);
      return <PrintLogView key={log.id} log={log} logs={data.logs} day={day} status={status} statuses={dayStatuses} auditLogEdits={data.auditLogEdits} />;
    });
  }, [data, day.id, shipStatuses, dayStatuses]);

  const logsLists = useMemo(() => {
    if (!data) {
      return [];
    }
    return data.logs.filter((log) => log.dayId == day.id).map((log) => {
      const status = shipStatusAtLog(shipStatuses, log.id);
      return <PrintLogListView key={log.id} log={log} day={day} status={status} statuses={dayStatuses} logs={data.logs} auditLogEdits={data.auditLogEdits} />;
    });
  }, [data, day.id, shipStatuses, dayStatuses]);

  const includeEdits = context?.includeEdits ?? false;
  const dayAuditLogEdits = useMemo(() => {
    return data?.auditLogEdits?.filter((e) => e.sourceType == IAuditLogEditSource.day && e.sourceId == day.id) ?? [];
  }, [data?.auditLogEdits, day.id]);

  const dayEditHistory = useMemo(() => {
    if (!includeEdits || dayAuditLogEdits.length === 0) {
      return [];
    }
    return computeEditHistory(dayAuditLogEdits, (field) => currentDayFieldValue(day, field));
  }, [includeEdits, dayAuditLogEdits, day]);

  const dayEditHistoryFieldFormat = useMemo(() => dayFieldFormat(shipUsers), [shipUsers]);
  const dayFieldLabels: Record<string, string> = {
    comment: t("comment"),
    crew: t("crew"),
    endLocation: t("end_location"),
    passengers: t("passengers"),
    skipper: t("skipper"),
    startLocation: t("start_location"),
  };
  const dayFieldLabel = (field: string) => dayFieldLabels[field] ?? field;

  return (
    <div className="PrintDayView container-boxed-inner">
      <h2>{app.format("date", date.toISOString())}</h2>
      <h4 className="print-text-normal-space-bottom">
        <span className="print-text-normal ">{tc("from_loc")}</span> {day.startLocation}
        <span className="print-text-normal print-distance-left">{tc("to_loc")}</span> {day.endLocation}
        <br />
        {/* <span className="print-distance-left"><BlockFieldInputsDivider /></span> */}
        <span className="print-text-normal">{t("timezone")}</span> {app.format("timezone", day.timezone)}
        <span className="print-distance-left"><BlockFieldInputsDivider /></span>
        {dayStatus !== IShipStatus.none && (
          <span className="print-text-normal print-distance-left">{t("status_day")} : </span>
        )}
        {dayStatus !== IShipStatus.none && <span className="">{statusButton(dayStatus)}</span>}
        {overnightStatus !== IShipStatus.none && (
          <span className="print-text-normal print-distance-left">{t("status_overnight")} : </span>
        )}
        {overnightStatus !== IShipStatus.none && <span className="">{statusButton(overnightStatus)}</span>}
      </h4>

      {context?.includeDayTrack && TrackViewData.hasPositions(tracks) && (
        <div className="PrintDayView-track">
          <TrackView fullscreen={false} noInteraction={true} tracks={tracks} timezone={day.timezone} />
        </div>
      )}

      <div className="PrintDayView-maindata container-boxed-inner">
        <h4>{tc("day_maindata")}</h4>
        <Block className="PrintDaysDataView ">
          <BlockRow>
            <BlockField label={t("skipper")} className="block-field-210">{getUserName(day.skipper)}</BlockField>
            <BlockField label={t("crew")} className="block-field-390">
              {getCrewNames(day.crew)}
            </BlockField>
            <BlockField label={t("passengers")} className="">{getCrewNames(day.passengers)}</BlockField>
          </BlockRow>
          <BlockRow>
            <BlockField label={t("comment")}>{day.comment}</BlockField>
          </BlockRow>
          {includeEdits && dayEditHistory.length > 0 && (
            <BlockRow>
              <EditHistory
                entries={dayEditHistory}
                fieldLabel={dayFieldLabel}
                fieldFormat={dayEditHistoryFieldFormat}
                open={true}
              />
            </BlockRow>
          )}
        </Block>
      </div>

      <div className="PrintDayView-statistics container-boxed-inner">
        <h4>{tc("day_statistics")}</h4>
        {day.calculated && <PrintCalculationsView calculated={day.calculated} />}
      </div>

      {context?.includeDayForecast && weatherNotes.length > 0 && (
        <Block className="PrintNoteView-day PrintNoteView-weather container-boxed-inner">
          {/* <h4>{tc("note_weather")}</h4> */}
          {weatherNotes}
        </Block>
      )}

      {context?.includeDayJournal && journalNotes.length > 0 && (
        <Block className="PrintNoteView-day PrintNoteView-journal container-boxed-inner">
          <h4>{tc("note_journal")}</h4>
          {journalNotes}
        </Block>
      )}

      {context?.includeDayTechnical && technicalNotes.length > 0 && (
        <Block className="PrintNoteView-day PrintNoteView-technical container-boxed-inner">
          <h4>{tc("note_technical")}</h4>
          {technicalNotes}
        </Block>
      )}

      {context?.includeDayWatchplan && watchGroups.length > 0 && (
        <div className="container-boxed-inner">
          <Block className="PrintWatchGroupView ">
            {watchGroups.length > 0 && (
              <>
                <h4>{tc("watch_groups")}</h4>
                <Block className="print-watch-groups-block">
                  {watchGroups}
                </Block>
              </>
            )}
          </Block>
          <Block className="PrintWatchShiftView">
            {watchShifts.length > 0 && (
              <>
                <h4>{tc("watch_shifts")}</h4>
                <Block className="print-watch-shifts-block">
                  {watchShifts}
                </Block>
              </>
            )}
          </Block>
        </div>
      )}

      {context?.includeLogs && (
        <div>
          {context?.includeDayLogListType == DayListType.short && (
            <Block className="PrintLogListView-block container-boxed-inner">
              <h3>{tc("log_entries")} {app.format("date", date.toISOString())}</h3>
              {logsLists}
            </Block>
          )}

          {context?.includeDayLogListType == DayListType.details && (
            <Block className="PrintLogView-block container-boxed-inner">
              <h3>{tc("log_entries")} {app.format("date", date.toISOString())}</h3>
              {logs}
            </Block>
          )}
        </div>
      )}
    </div>
  );
}
